import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import InstructorAnalyticsService from '../../services/instructorAnalyticsService';
import InstructorService, { LiveSession } from '../../services/instructorService';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Calendar,
  MessageSquare,
  Award,
  Clock,
  Eye,
  Star,
  ChevronRight,
  Play,
  PlusCircle,
  Plus,
  Edit,
  BarChart3,
  DollarSign,
  TrendingUpIcon,
  UserCheck,
  CheckCircle,
  Target,
  Brain
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

interface DashboardStats {
  totalCourses: number;
  totalStudents: number;
  totalRevenue: number;
  averageRating: number;
  pendingMessages: number;
  upcomingSessions: number;
  potentialTotalRevenue: number; // Ajout pour aligner avec Courses page
  averageOfAverageRatings: number; // Ajout pour aligner avec Courses page
  studentsGrowth: number;
  coursesGrowth: number;
}

interface RecentActivity {
  id: string;
  type: 'enrollment' | 'completion' | 'review' | 'message';
  title: string;
  description: string;
  timestamp: string;
  studentName?: string;
  courseName?: string;
  rating?: number;
}

interface UpcomingSession {
  id: string;
  title: string;
  courseName: string;
  scheduledAt: string;
  duration: number;
  studentsCount: number;
}

const InstructorDashboardPage: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { instructor, hasPermission, isLoading } = useInstructorPermissions();
  const { tenant } = useTenantContext();
  
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: 0,
    averageRating: 0,
    pendingMessages: 0,
    upcomingSessions: 0,
    potentialTotalRevenue: 0,
    averageOfAverageRatings: 0,
    studentsGrowth: 0,
    coursesGrowth: 0
  });
  
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  // Supprime le loading local pour éviter le spinner infini. On s'appuie sur isLoading du contexte

  useEffect(() => {
    const loadDashboardData = async () => {
      if (instructor && tenant?.id) {
        try {
          // Charger les analytics générales
          const analytics = await InstructorAnalyticsService.getAnalyticsOverview(
            tenant.id,
            instructor.id,
            '1y' // Étendue à 1 an pour éviter 0 si pas d'inscriptions récentes
          );
          
          setStats(prev => ({
            ...prev,
            totalCourses: analytics.totalCourses,
            totalStudents: analytics.totalStudents,
            totalRevenue: analytics.totalRevenue, // Garde le revenu réel pour info si besoin
            potentialTotalRevenue: analytics.potentialTotalRevenue, // Utilise le revenu potentiel
            averageRating: analytics.averageRating, // Garde la note réelle pour info si besoin
            averageOfAverageRatings: analytics.averageOfAverageRatings, // Utilise la moyenne des moyennes
            studentsGrowth: analytics.studentsGrowth,
            coursesGrowth: analytics.coursesGrowth,
            pendingMessages: 0, // TODO: Implémenter avec service de messages
          }));

          // Charger les sessions à venir (status scheduled)
          const sessions = await InstructorService.getInstructorLiveSessions(tenant.id, instructor.id, { status: 'scheduled', limit: 5 }) as LiveSession[];
          
          setUpcomingSessions(sessions.map((session: LiveSession) => ({
            id: session.id,
            title: session.title,
            courseName: session.course?.title || 'Cours non défini',
            scheduledAt: session.scheduled_at,
            duration: session.duration_minutes,
            studentsCount: session.attendance_count
          })));

          // Mettre à jour le compteur des sessions à venir
          setStats(prev => ({ ...prev, upcomingSessions: sessions.length }));

          // Charger les activités récentes (avis récents)
          const reviewAnalytics = await InstructorAnalyticsService.getReviewAnalytics(
            tenant.id,
            instructor.id,
            5
          );
          
          const activities: RecentActivity[] = reviewAnalytics.recentReviews.map(review => ({
            id: review.id,
            type: 'review' as const,
            title: 'Nouvel avis',
            description: `${review.studentName} a laissé un avis ${review.rating} étoiles sur "${review.courseName}"`,
            timestamp: review.date,
            studentName: review.studentName,
            courseName: review.courseName,
            rating: review.rating
          }));
          
          setRecentActivities(activities);
          
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
          toast({
            title: 'Erreur',
            description: 'Impossible de charger les données du tableau de bord',
            variant: 'destructive'
          });
        }
      }
    };
    
    if (!isLoading && instructor && tenant?.id) {
      loadDashboardData();
    }
  }, [instructor, tenant, isLoading, toast]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'enrollment': return <Users className="h-4 w-4 text-blue-500" />;
      case 'completion': return <Award className="h-4 w-4 text-green-500" />;
      case 'review': return <Star className="h-4 w-4 text-yellow-500" />;
      case 'message': return <MessageSquare className="h-4 w-4 text-purple-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-8">
        {/* En-tête avec bienvenue et création de cours */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white">Tableau de bord instructeur</h1>
            <p className="text-gray-400">Bienvenue, {instructor?.name || 'Instructeur'} 👋</p>
          </div>
          <Button 
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
            onClick={() => navigate('/instructor/courses/new')}
          >
            <Plus className="h-5 w-5 mr-2" />
            Créer un cours
          </Button>
        </div>
      
        {/* Statistiques modernes */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
      >
        {[
          {
            title: 'Cours',
            value: stats.totalCourses,
            icon: BookOpen,
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-500/10',
            textColor: 'text-blue-400',
            description: 'Total de vos cours'
          },
          {
            title: 'Étudiants',
            value: stats.totalStudents,
            icon: Users,
            color: 'from-purple-500 to-purple-600',
            bgColor: 'bg-purple-500/10',
            textColor: 'text-purple-400',
            description: 'Inscrits à vos cours'
          },
          {
            title: 'Revenus',
            value: formatCurrency(stats.potentialTotalRevenue),
            icon: DollarSign,
            color: 'from-green-500 to-green-600',
            bgColor: 'bg-green-500/10',
            textColor: 'text-green-400',
            description: 'Revenus potentiels'
          },
          {
            title: 'Note moyenne',
            value: stats.averageOfAverageRatings.toFixed(1),
            icon: Star,
            color: 'from-amber-500 to-amber-600',
            bgColor: 'bg-amber-500/10',
            textColor: 'text-amber-400',
            description: 'De vos cours'
          }
        ].map((stat, index) => {

            const Icon = stat.icon;
            
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30">
                  <CardContent className="p-6">
                    {/* Gradient background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
                    
                    {/* Icon container */}
                    <div className={`${stat.bgColor} w-12 h-12 rounded-xl flex items-center justify-center mb-4 relative z-10`}>
                      <Icon className={`h-6 w-6 ${stat.textColor}`} />
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-400">
                          {stat.title}
                        </h3>
                      </div>
                      
                      <div className="flex items-baseline space-x-2 mb-2">
                        <span className="text-3xl font-bold text-white">
                          {stat.value}
                        </span>
                      </div>
                      
                      <p className="text-xs text-gray-500">
                        {stat.description}
                      </p>

                      {/* Indicateurs de croissance supprimés sur demande */}
                    </div>

                    {/* Decorative element */}
                    <div className={`absolute -right-4 -top-4 w-20 h-20 bg-gradient-to-br ${stat.color} opacity-10 rounded-full`} />
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        }
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activités récentes avec design moderne */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-white">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <MessageSquare className="h-5 w-5 text-blue-400" />
                  </div>
                  Activités récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {recentActivities.length > 0 ? (
                    recentActivities.map((activity) => (
                      <motion.div 
                        key={activity.id}
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 p-4 border border-gray-700/50 rounded-lg bg-gray-800/30 hover:bg-gray-800/50 transition-all duration-300 group"
                      >
                        <div className="p-2 rounded-full bg-blue-500/10 mt-1">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-white">{activity.title}</h4>
                              <p className="text-sm text-gray-400 mt-1">{activity.description}</p>
                            </div>
                            <div className="text-sm text-gray-400 sm:text-right mt-2 sm:mt-0">
                              {formatDate(activity.timestamp)}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            {activity.studentName && (
                              <Badge variant="secondary" className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/30">
                                <UserCheck className="h-3 w-3 mr-1" />
                                {activity.studentName}
                              </Badge>
                            )}
                            {activity.courseName && (
                              <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">
                                {activity.courseName}
                              </Badge>
                            )}
                            {activity.type === "review" && activity.rating && (
                              <Badge variant="secondary" className="flex items-center gap-1 bg-amber-500/20 text-amber-300 border-amber-500/30">
                                <Star className="h-3 w-3 fill-current" />
                                {activity.rating}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                      <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
                      <p className="text-center">Aucune activité récente</p>
                    </div>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-gray-400 hover:text-white hover:bg-gray-800/50"
                  onClick={() => navigate('/instructor/activities')}
                >
                  Voir toutes les activités
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sessions à venir avec design moderne */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-white">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Calendar className="h-5 w-5 text-green-400" />
                  </div>
                  Sessions à venir
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {upcomingSessions.length > 0 ? (
                    upcomingSessions.map((session) => (
                      <motion.div 
                        key={session.id}
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 border border-gray-700/50 rounded-lg bg-gray-800/30 hover:bg-gray-800/50 transition-all duration-300 group"
                      >
                        <div className="p-2 rounded-full bg-green-500/10">
                          <Play className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <h4 className="font-medium text-white">{session.courseName}</h4>
                            <Badge variant="secondary" className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                              Planifiée
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">{session.title}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-500">{formatDate(session.scheduledAt)}</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="self-end sm:self-auto mt-2 sm:mt-0 text-gray-400 hover:text-white hover:bg-gray-700/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          onClick={() => navigate(`/instructor/sessions/${session.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto opacity-50" />
                      <p className="mt-2">Aucune session planifiée</p>
                    </div>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-gray-400 hover:text-white hover:bg-gray-800/50"
                  onClick={() => navigate('/instructor/sessions')}
                >
                  Voir toutes les sessions
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

      {/* Actions rapides avec design amélioré */}
      <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600">
              <CheckCircle className="h-5 w-5 text-white" />
            </div>
              Actions rapides
            </CardTitle>
            <CardDescription className="text-gray-400">
              Accès rapide aux fonctionnalités les plus utilisées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="h-24 flex flex-col items-center justify-center gap-3 bg-gray-800/30 border-gray-700/50 hover:bg-gray-800/50 hover:border-blue-500/50 transition-all duration-300"
                onClick={() => navigate('/instructor/courses')}
              >
                <BookOpen className="h-8 w-8 text-blue-400" />
                <span className="font-medium text-white">Mes Cours</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-24 flex flex-col items-center justify-center gap-3 bg-gray-800/30 border-gray-700/50 hover:bg-gray-800/50 hover:border-purple-500/50 transition-all duration-300"
                onClick={() => navigate('/instructor/students')}
              >
                <Users className="h-8 w-8 text-purple-400" />
                <span className="font-medium text-white">Mes Étudiants</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-24 flex flex-col items-center justify-center gap-3 bg-gray-800/30 border-gray-700/50 hover:bg-gray-800/50 hover:border-green-500/50 transition-all duration-300"
                onClick={() => navigate('/instructor/earnings')}
              >
                <DollarSign className="h-8 w-8 text-green-400" />
                <span className="font-medium text-white">Revenus</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </InstructorLayout>
  );
};

export default InstructorDashboardPage;
